import { CheckboxFilter, PopoverMechanism } from "@netapp/bxp-design-system-react";
import { DropdownArrowDownIcon } from "@netapp/bxp-style/react-icons/Navigation";
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { globalSearch as i18nNamespace } from "../../../i18n/namespace";
import {
    CONSOLE_OBJECTS_PREVIEW_LIMIT,
    CONSOLE_OBJECTS_TYPE_SEE_ALL_LIMIT,
    ONTAP_OBJECT_TYPE_FILTERS,
    SEARCH_MIN_LENGTH
} from "../consoleObjects";
import styles from "../GlobalSearch.module.scss";

export const defaultObjectTypeFilter = () => Object.fromEntries(ONTAP_OBJECT_TYPE_FILTERS.map(item => [item.label, true]));

export const apiTypesForFilter = values => ONTAP_OBJECT_TYPE_FILTERS.filter(item => values?.[item.label]).map(item => item.apiType);

export const narrowedObjectTypes = values => {
    const objectTypes = apiTypesForFilter(values);
    if (!objectTypes.length || objectTypes.length === ONTAP_OBJECT_TYPE_FILTERS.length) {
        return undefined;
    }
    return objectTypes;
};

export const ObjectTypeFilterContext = createContext(null);

let suppressCloseUntil = 0;

export const suppressGlobalSearchClose = () => {
    suppressCloseUntil = Date.now() + 50;
};

export const isGlobalSearchCloseSuppressed = () => Date.now() < suppressCloseUntil;

export const useObjectTypeFilter = () => useContext(ObjectTypeFilterContext);

export const ObjectTypeFilterControl = ({ values, onChange }) => {
    const { t } = useTranslation(i18nNamespace);
    const typeLabel = item => t(`object_types.${item.labelKey}`);
    const selectedObjectTypes = ONTAP_OBJECT_TYPE_FILTERS.filter(item => values?.[item.label]);
    const allObjectTypesSelected = selectedObjectTypes.length === ONTAP_OBJECT_TYPE_FILTERS.length;
    const filterLabel = allObjectTypesSelected
        ? t("all_types")
        : `${selectedObjectTypes[0] ? typeLabel(selectedObjectTypes[0]) : ""}${selectedObjectTypes.length > 1 ? `, +${selectedObjectTypes.length - 1}` : ""}`;
    const filterOptions = ONTAP_OBJECT_TYPE_FILTERS.map(item => ({ value: item.label, label: typeLabel(item) }));

    return <PopoverMechanism
        triggerOnClick
        renderPosition="left"
        triggerClassName={styles["filter-trigger"]}
        Popover={CheckboxFilter}
        filterOptions={filterOptions}
        filterState={{ values }}
        updateFilter={onChange}
        Trigger={() => <span className={styles["filter-label"]}>
            <>{t("filter_objects")}:</>
            {` ${filterLabel}`}
            <DropdownArrowDownIcon aria-hidden="true" />
        </span>}
    />;
};

export const ObjectTypeFilterProvider = ({ children }) => {
    const dispatch = useDispatch();
    const searchTerm = useSelector(state => state.globalSearch.searchTerm);
    const isOpen = useSelector(state => state.globalSearch.isOpen);
    const isSearchPerformed = useSelector(state => state.globalSearch.isSearchPerformed);
    const [objectTypeFilter, setObjectTypeFilter] = useState(defaultObjectTypeFilter);
    const consoleExpandedRef = useRef(false);

    useEffect(() => {
        if (!isOpen && !isSearchPerformed) {
            setObjectTypeFilter(defaultObjectTypeFilter());
        }
    }, [isOpen, isSearchPerformed]);

    const applyObjectFilter = useCallback(values => {
        suppressGlobalSearchClose();
        const nextFilter = values || {};
        const objectTypes = apiTypesForFilter(nextFilter);
        if (!objectTypes.length) {
            return;
        }
        setObjectTypeFilter(nextFilter);
        if (!searchTerm || searchTerm.trim().length < SEARCH_MIN_LENGTH) {
            return;
        }
        const narrowed = narrowedObjectTypes(nextFilter);
        if (consoleExpandedRef.current) {
            dispatch({
                type: "GLOBAL-SEARCH/CONSOLE-OBJECTS/SEE-ALL",
                payload: {
                    searchTerm,
                    limit: CONSOLE_OBJECTS_TYPE_SEE_ALL_LIMIT,
                    ...(narrowed ? { objectTypes: narrowed } : {})
                }
            });
            return;
        }
        dispatch({
            type: "GLOBAL-SEARCH/CONSOLE-OBJECTS/SEARCH",
            payload: {
                searchTerm,
                limit: CONSOLE_OBJECTS_PREVIEW_LIMIT,
                objectTypes
            }
        });
    }, [dispatch, searchTerm]);

    const value = useMemo(() => ({
        objectTypeFilter,
        setObjectTypeFilter,
        applyObjectFilter,
        consoleExpandedRef
    }), [objectTypeFilter, applyObjectFilter]);

    return <ObjectTypeFilterContext.Provider value={value}>{children}</ObjectTypeFilterContext.Provider>;
};
